// Sample payload shapes for execute_workflow_by_name, extracted from the
// SafetyCulture/temporal-workflows repo's Go source (workflow function
// parameter types -- proto messages in s12/tools/support/v1/workflows.proto
// for the ~14 workflows that share the common support.*Request types, and
// local Go structs under workflows/ or internal/migration/ for the rest).
//
// IMPORTANT CAVEATS -- these are a starting reference, NOT a verified wire
// format:
// - "proto" entries use the Go standard-library `json:` struct tag emitted by
//   protoc-gen-go, which is the ORIGINAL snake_case proto field name (e.g.
//   "users_to_change"), NOT the protojson camelCase name also embedded in the
//   same field's `protobuf:` tag (e.g. "usersToChange"). This was previously
//   assumed to be protojson camelCase and was wrong for all "proto" entries;
//   verified 2026-07 by reading the generated Go structs in s12-apis-go and by
//   confirming via live Temporal execution that the backend's ExecuteWorkflowByName
//   (api-tools-support, internal/governance/temporal.go) round-trips the raw
//   payload through a generic google.protobuf.Struct and ultimately decodes it
//   into the concrete workflow request type with plain encoding/json (no custom,
//   proto-aware DataConverter is registered) -- so the struct tag's exact key
//   wins. One known exception: a field can be genuinely non-snake_case if the
//   .proto itself declares it that way (e.g. ResetOrgTemplatesAccess's
//   "backupFolderName") -- check the source file if a "proto" entry looks
//   inconsistent rather than assuming it's a typo.
// - "go-struct" entries have NO JSON tags in source, so they use the exact
//   Go field name as-is (often PascalCase, e.g. "InspectionIDs") -- this is
//   what plain encoding/json.Unmarshal would expect, but the actual backend
//   service that dispatches execute_workflow_by_name payloads lives in a
//   different repo, and its exact unmarshal mechanism per workflow is
//   unverified from here.
// - Nested/enum/unknown field types are rendered as "<TypeName>" placeholders
//   rather than guessed values -- check the linked source file for specifics.
//   Enum fields (e.g. Environment, IdentityType, Region) also have no custom
//   JSON marshaling, so they require the bare numeric enum value (e.g. `2`),
//   not the string constant name (e.g. NOT "ENVIRONMENT_SANDPIT") -- check the
//   proto's enum declaration for the right number.
// - This list will go stale as temporal-workflows evolves; regenerate by
//   re-running the extraction script if it's materially out of date.
//
// Always review and edit the loaded sample before executing -- it populates
// the same editable Payload fields/JSON as any other input, never auto-runs.
export interface TemporalWorkflowSample {
  sourceKind: "proto" | "go-struct";
  sourceType: string;
  sourceFile: string;
  sample: Record<string, unknown>;
}

export const TEMPORAL_WORKFLOW_SAMPLES: Record<string, TemporalWorkflowSample> =
  {
    AlexandriaCopyWorkflow: {
      sample: {
        CopyRequest: "<OrgDataCopyRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_copy.go",
      sourceKind: "go-struct",
      sourceType: "CopyWorkflowRequest",
    },
    AlexandriaMappingWorkflow: {
      sample: {
        Domain: "<migration.OrgDataDomainType>",
        MapRequest: "<OrgDataMapRequest>",
      },
      sourceFile: "internal/migration/workflows/org_data_map.go",
      sourceKind: "go-struct",
      sourceType: "MappingWorkflowRequest",
    },
    ArchiveAlexandriaDocuments: {
      sample: {
        DocumentIDs: [],
        Region: "<support.Region>",
      },
      sourceFile: "workflows/workflow-archive-alexandria-documents.go",
      sourceKind: "go-struct",
      sourceType: "ArchiveAlexandriaDocumentsRequest",
    },
    ArchiveDocumentByOwner: {
      sample: {
        Inspections: false,
        OrgID: "",
        OwnerID: "",
        Region: "<support.Region>",
        Templates: false,
      },
      sourceFile: "workflows/workflow-archive-document-by-owner.go",
      sourceKind: "go-struct",
      sourceType: "ArchiveDocumentByOwnerRequest",
    },
    ArchiveDuplicateSystemFieldsWorkflow: {
      sample: {
        region: "",
        targets: [],
      },
      sourceFile: "workflows/workflow-archive-duplicate-system-fields.go",
      sourceKind: "proto",
      sourceType: "support.ArchiveDuplicateSystemFieldsRequest",
    },
    Auth0Backup: {
      sample: {},
      sourceFile: "workflows/workflow-auth0-backup.go",
      sourceKind: "proto",
      sourceType: "support.Auth0BackupRequest",
    },
    BackfillTemplatesItemsLabelProjection: {
      sample: {
        Environment: "",
        Region: "",
        S3FileName: "",
      },
      sourceFile:
        "workflows/workflow-backfill-templates-items-label-projection.go",
      sourceKind: "proto",
      sourceType: "support.BackfillTemplatesItemsLabelProjectionRequest",
    },
    BackfillTemplatesItemsLabelProjectionStarter: {
      sample: {
        Environment: "",
        Region: "",
        S3FileName: "",
      },
      sourceFile:
        "workflows/workflow-backfill-templates-items-label-projection-starter.go",
      sourceKind: "proto",
      sourceType: "support.BackfillTemplatesItemsLabelProjectionStarterRequest",
    },
    BlockOrganisation: {
      sample: {
        OrgID: "",
        Region: "<support.Region>",
      },
      sourceFile: "workflows/workflow-org-block-organisation.go",
      sourceKind: "go-struct",
      sourceType: "BlockOrganisationRequest",
    },
    BulkAddUsersToGroups: {
      sample: {
        region: "<Region>",
        users_to_add: [],
      },
      sourceFile: "workflows/workflow-bulk-add-users-to-groups.go",
      sourceKind: "proto",
      sourceType: "support.BulkAddUsersToGroupsRequest",
    },
    BulkAssignPermissionSetsWorkflow: {
      sample: {
        OrgID: "",
        Region: "",
        UserPermissionSet: "<map>",
      },
      sourceFile: "workflows/workflow-bulk-assign-permissions.go",
      sourceKind: "go-struct",
      sourceType: "BulkAssignPermissionSetsRequest",
    },
    BulkBackupDocumentsToS3: {
      sample: {
        BackupID: "",
        DocIDs: [],
        DocType: "<DocType>",
        Region: "<support.Region>",
      },
      sourceFile: "workflows/workflow-bulk-backup-documents-to-s3.go",
      sourceKind: "go-struct",
      sourceType: "BulkBackupDocumentsToS3Request",
    },
    BulkCSVWorkflowDispatcher: {
      sample: {
        continue_on_error: false,
        main_batch_size: 0,
        max_parallel_target_workflows: 0,
        processing_time_per_row_seconds: 0,
        region: "",
        request_reference: "",
        s3_file_name: "",
        sc_employee: {
          email: "",
          name: "",
        },
        sub_batch_size: 0,
        target_workflow_name: "",
        target_workflow_requests_field: "",
      },
      sourceFile: "workflows/workflow-bulk-csv-dispatcher.go",
      sourceKind: "proto",
      sourceType: "support.BulkCSVWorkflowDispatcherRequest",
    },
    BulkChangeEmailAddress: {
      sample: {
        region: "",
        users_to_change: [],
      },
      sourceFile: "workflows/workflow-bulk-change-email-address.go",
      sourceKind: "proto",
      sourceType: "support.BulkChangeEmailAddressRequest",
    },
    BulkChangeEmailSingleBatch: {
      sample: {
        region: "",
        users_to_change: [],
      },
      sourceFile: "workflows/workflow-bulk-change-email-address.go",
      sourceKind: "proto",
      sourceType: "support.BulkChangeEmailAddressRequest",
    },
    BulkCleanupChargifySubscription: {
      sample: {
        requests: [],
      },
      sourceFile: "workflows/workflow-bulk-cleanup-chargify-subscription.go",
      sourceKind: "proto",
      sourceType: "support.BulkCleanupChargifySubscriptionRequest",
    },
    BulkCopyTemplatePermissionAndAutoShare: {
      sample: {
        DestinationTemplateIDs: [],
        OrgID: "",
        Purpose: "",
        Region: "",
        SourceTemplateID: "",
      },
      sourceFile:
        "workflows/workflow-bulk-copy-template-permission-and-autoshare.go",
      sourceKind: "go-struct",
      sourceType: "BulkCopyTemplatePermissionAndAutoShareRequest",
    },
    BulkCreateActions: {
      sample: {
        MaxDaysInPast: 0,
        NumActions: 0,
        OrgID: "",
        Region: "<support.Region>",
        SiteIDs: [],
        Titles: [],
        UserIDs: [],
      },
      sourceFile: "workflows/workflow-bulk-create-actions.go",
      sourceKind: "go-struct",
      sourceType: "BulkCreateActionsRequest",
    },
    BulkCreateActionsWithConcurrency: {
      sample: {
        Concurrency: 0,
      },
      sourceFile: "workflows/workflow-bulk-create-actions.go",
      sourceKind: "go-struct",
      sourceType: "BulkCreateActionsWithConcurrencyRequest",
    },
    BulkCreateAssetReadings: {
      sample: {
        AssetIDs: [],
        IncludeOdometer: false,
        IncludeUsageTime: false,
        MaxDaysInPast: 0,
        OdometerUnitSymbol: "<common.MeasurementUnitSymbol>",
        OrgID: "",
        Region: "<support.Region>",
        TemplateID: "",
        TimeUnitSymbol: "<common.MeasurementUnitSymbol>",
        UserIDs: [],
      },
      sourceFile: "workflows/workflow-bulk-create-assets.go",
      sourceKind: "go-struct",
      sourceType: "BulkCreateAssetReadingsRequest",
    },
    BulkCreateAssetReadingsWithConcurrency: {
      sample: {
        Concurrency: 0,
      },
      sourceFile: "workflows/workflow-bulk-create-assets.go",
      sourceKind: "go-struct",
      sourceType: "BulkCreateAssetReadingsWithConcurrencyRequest",
    },
    BulkCreateAssetTypes: {
      sample: {
        AssetTypeNames: [],
        OrgID: "",
        Region: "<support.Region>",
      },
      sourceFile: "workflows/workflow-bulk-create-asset-types.go",
      sourceKind: "go-struct",
      sourceType: "BulkCreateAssetTypesRequest",
    },
    BulkCreateAssetTypesAndData: {
      sample: {
        MaxDaysInPast: 0,
        OrgID: "",
        Region: "<support.Region>",
        SiteIDs: [],
        TypeAndDataRequests: [],
        UserIDs: [],
      },
      sourceFile: "workflows/workflow-bulk-create-assets.go",
      sourceKind: "go-struct",
      sourceType: "BulkCreateAssetTypesAndDataRequest",
    },
    BulkCreateAssets: {
      sample: {
        AssetsRequests: [],
        MaxDaysInPast: 0,
        OrgID: "",
        Region: "<support.Region>",
        SiteIDs: [],
        UserIDs: [],
      },
      sourceFile: "workflows/workflow-bulk-create-assets.go",
      sourceKind: "go-struct",
      sourceType: "BulkCreateAssetsRequest",
    },
    BulkCreateDemoOrgData: {
      sample: {
        BulkCreateActionsRequest: {
          MaxDaysInPast: 0,
          NumActions: 0,
          OrgID: "",
          Region: "<support.Region>",
          SiteIDs: [],
          Titles: [],
          UserIDs: [],
        },
        BulkCreateAssetReadingsRequests: [],
        BulkCreateAssetTypesAndDataRequest: {
          MaxDaysInPast: 0,
          OrgID: "",
          Region: "<support.Region>",
          SiteIDs: [],
          TypeAndDataRequests: [],
          UserIDs: [],
        },
        BulkCreateIssuesRequest: {
          MaxDaysInPast: 0,
          OrgID: "",
          Region: "<support.Region>",
          SiteIDs: [],
          Titles: [],
          TotalNumIssues: 0,
          UserIDs: [],
        },
        BulkStartInspectionsRequest: {
          MaxDaysInPast: 0,
          NumInspections: 0,
          OrgID: "",
          ParentWorkflowRunID: "",
          Region: "<support.Region>",
          SiteIDs: [],
          TemplateIds: [],
          UserIDs: [],
        },
        OrgID: "",
        Region: "<support.Region>",
        SiteIDs: [],
      },
      sourceFile: "workflows/workflow-bulk-create-demo-org-data.go",
      sourceKind: "go-struct",
      sourceType: "BulkCreateDemoOrgDataRequest",
    },
    BulkCreateIssues: {
      sample: {
        MaxDaysInPast: 0,
        OrgID: "",
        Region: "<support.Region>",
        SiteIDs: [],
        Titles: [],
        TotalNumIssues: 0,
        UserIDs: [],
      },
      sourceFile: "workflows/workflow-bulk-create-issues.go",
      sourceKind: "go-struct",
      sourceType: "BulkCreateIssuesRequest",
    },
    BulkCreateOrgs: {
      sample: {
        Domain: "",
        EmailLocalAddress: "",
        EmailSubAddress: "",
        ExcludeFromAnalytics: false,
        FirstName: "",
        LastName: "",
        NumOrgs: "<uint>",
        OrgName: "",
        Region: "",
        StartCounter: "<uint>",
        TestAccount: false,
      },
      sourceFile: "workflows/workflow-bulk-create-org.go",
      sourceKind: "proto",
      sourceType: "support.BulkCreateOrgsRequest",
    },
    BulkCreateUsers: {
      sample: {
        data: [],
        env: "",
        region: "",
      },
      sourceFile: "workflows/workflow-bulk-create-users.go",
      sourceKind: "proto",
      sourceType: "support.BulkCreateUsersRequest",
    },
    BulkDeactivateUsers: {
      sample: {
        NumberOfBatches: 0,
        OrgID: "",
        Region: "",
        UserIDs: [],
      },
      sourceFile: "workflows/workflow-bulk-deactivate-users.go",
      sourceKind: "go-struct",
      sourceType: "BulkDeactivateUsersRequest",
    },
    BulkDeactivateUsersAcrossOrgs: {
      sample: {
        requests: [],
      },
      sourceFile: "workflows/workflow-bulk-deactivate-users-across-orgs.go",
      sourceKind: "proto",
      sourceType: "support.BulkDeactivateUsersAcrossOrgsRequest",
    },
    BulkDeleteMediaForCPR: {
      sample: {
        Env: "",
        MediaIDs: [],
        OrgID: "",
        Reference: "",
        Region: "",
      },
      sourceFile: "workflows/workflow-org-deletion.go",
      sourceKind: "proto",
      sourceType: "support.BulkDeleteMediaForCPRRequest",
    },
    BulkDeleteUserWorkflow: {
      sample: {
        Env: "",
        MustRemoveUser: false,
        OrgID: "",
        Region: "",
        ReplacementUserID: "",
        UserIDs: [],
      },
      sourceFile: "workflows/workflow-bulk-delete-users.go",
      sourceKind: "go-struct",
      sourceType: "BulkDeleteUserRequest",
    },
    BulkFetchOrgTenancy: {
      sample: {
        BatchSize: 0,
        OrgIDs: [],
        Reference: "",
        Region: "",
      },
      sourceFile: "workflows/workflow-bulk-org-tenancy.go",
      sourceKind: "go-struct",
      sourceType: "BulkFetchOrgTenancyRequest",
    },
    BulkForceUserLogout: {
      sample: {
        Users: [],
      },
      sourceFile: "workflows/workflow-bulk-force-user-logout.go",
      sourceKind: "proto",
      sourceType: "support.BulkForceUserLogoutRequest",
    },
    BulkMigrateOrgsToZuora: {
      sample: {
        BatchName: "",
        BatchSize: 0,
        CreatedAfter: "<time.Time>",
        CreatedBefore: "<time.Time>",
        ExclusionList: [],
        InclusionList: [],
        NumberOfBatches: 0,
        Region: "",
        RunMigrations: false,
        ShouldPersist: false,
        SubscriptionType: "<adminsubscriptions.SubscriptionType>",
      },
      sourceFile: "workflows/workflow-bulk-migrate-orgs-to-zuora.go",
      sourceKind: "go-struct",
      sourceType: "BulkMigrateOrgsToZuoraRequest",
    },
    BulkMigrateTemplates: {
      sample: {
        DestRegion: "<support.Region>",
        DestUserID: "",
        SourceRegion: "<support.Region>",
        TemplateIDs: [],
      },
      sourceFile: "workflows/workflow-bulk-migrate-templates.go",
      sourceKind: "proto",
      sourceType: "support.BulkMigrateTemplatesRequest",
    },
    BulkMigrateToZuora: {
      sample: {
        requests: [],
      },
      sourceFile: "workflows/workflow-bulk-migrate-to-zuora.go",
      sourceKind: "proto",
      sourceType: "support.BulkMigrateToZuoraRequest",
    },
    BulkMoveGRS: {
      sample: {
        global_response_set_ids: [],
        region: "<Region>",
        sc_employee: {
          email: "",
          name: "",
        },
        source_org_id: "",
        target_org_id: "",
      },
      sourceFile: "workflows/workflow-bulk-move-grs.go",
      sourceKind: "proto",
      sourceType: "support.BulkMoveGRSRequest",
    },
    BulkOrgDeletion: {
      sample: {
        Env: "",
        OrgIDs: [],
        Purpose: "",
        Region: "",
        RequestReference: "",
        Requester: "",
      },
      sourceFile: "workflows/workflow-bulk-org-deletion.go",
      sourceKind: "go-struct",
      sourceType: "BulkOrgDeletionRequest",
    },
    BulkPatchUserRoles: {
      sample: {
        data: [],
        env: "",
        region: "",
      },
      sourceFile: "workflows/workflow-bulk-patch-user-roles.go",
      sourceKind: "proto",
      sourceType: "support.BulkPatchUserRolesRequest",
    },
    BulkProvisionAiCredits: {
      sample: {
        BatchSize: 0,
        BillingProvider: "<subscriptionsv1.BillingProvider>",
        CarriedBatchesProcessed: 0,
        CarriedFailedCount: 0,
        CarriedFailedItems: [],
        CarriedTotalOrgsFound: 0,
        CreatedAfter: "<time.Time>",
        CreatedBefore: "<time.Time>",
        MaxActiveUsers: 0,
        NumberOfBatches: 0,
        OrgIDs: [],
        PageToken: "",
        Region: "",
        RunSyncs: false,
        SubscriptionType: "<adminsubscriptions.SubscriptionType>",
      },
      sourceFile: "workflows/workflow-bulk-provision-ai-credits.go",
      sourceKind: "go-struct",
      sourceType: "BulkProvisionAiCreditsRequest",
    },
    BulkProvisionAiCreditsAcrossWindows: {
      sample: {
        BatchSize: 0,
        BillingProvider: "<subscriptionsv1.BillingProvider>",
        EndYear: 0,
        MaxActiveUsers: 0,
        MaxParallelWindows: 0,
        NumberOfBatches: 0,
        OrgIDs: [],
        Region: "",
        RunSyncs: false,
        StartYear: 0,
        SubscriptionType: "<adminsubscriptions.SubscriptionType>",
        WindowMonths: 0,
      },
      sourceFile: "workflows/workflow-bulk-provision-ai-credits.go",
      sourceKind: "go-struct",
      sourceType: "BulkProvisionAiCreditsAcrossWindowsRequest",
    },
    BulkRefreshInspectionProjections: {
      sample: {
        Inspections: [],
        Region: "<support.Region>",
      },
      sourceFile: "workflows/workflow-bulk-refresh-inspection-projections.go",
      sourceKind: "go-struct",
      sourceType: "BulkRefreshInspectionProjectionsRequest",
    },
    BulkRemoveOrgUsersFromBounceList: {
      sample: {
        org_id: "",
        reason: "",
        region: "<Region>",
      },
      sourceFile: "workflows/workflow-bulk-remove-bounces.go",
      sourceKind: "proto",
      sourceType: "support.BulkRemoveOrgUsersFromBounceListRequest",
    },
    BulkResetOrgTemplatesAccess: {
      sample: {
        BackupFolderName: "",
        OrgIDs: [],
        Region: "",
      },
      sourceFile: "workflows/workflow-reset-org-template-access.go",
      sourceKind: "proto",
      sourceType: "support.BulkResetOrgTemplatesAccessRequest",
    },
    BulkRestoreDeletedInspections: {
      sample: {
        InspectionIDs: [],
        Region: "<support.Region>",
      },
      sourceFile: "workflows/workflow-bulk-restore-deleted-inspections.go",
      sourceKind: "go-struct",
      sourceType: "BulkRestoreDeletedInspectionsRequest",
    },
    BulkRestoreInspections: {
      sample: {
        InspectionIDs: [],
        Region: "<support.Region>",
        RequestReference: "",
        Requester: "",
      },
      sourceFile: "workflows/workflow-bulk-restore-inspections.go",
      sourceKind: "go-struct",
      sourceType: "BulkRestoreInspectionsRequest",
    },
    BulkRestoreInspectionsFromS3: {
      sample: {
        Environment: "",
        InspectionIDs: [],
        Region: "",
        S3FolderName: "",
      },
      sourceFile: "workflows/workflow-bulk-restore-inspections-from-s3.go",
      sourceKind: "proto",
      sourceType: "support.BulkRestoreInspectionsFromS3Request",
    },
    BulkRestoreInspectionsProcessInspections: {
      sample: {
        BackupID: "",
        BackupOperationName: "",
        InspectionIDs: [],
        Region: "<support.Region>",
      },
      sourceFile: "workflows/workflow-bulk-restore-inspections.go",
      sourceKind: "go-struct",
      sourceType: "BulkRestoreInspectionsDocumentsRequest",
    },
    BulkRestoreScheduleStatus: {
      sample: {
        OrgID: "",
        Region: "<support.Region>",
        ScheduleIDs: [],
        TempSiteID: "",
        UserID: "",
      },
      sourceFile: "workflows/workflow-bulk-restore-schedule-status.go",
      sourceKind: "go-struct",
      sourceType: "BulkRestoreScheduleStatusRequest",
    },
    BulkSetInspectionAccess: {
      sample: {
        Requests: [],
      },
      sourceFile: "workflows/workflow-bulk-set-inspection-permissions.go",
      sourceKind: "go-struct",
      sourceType: "BulkSetInspectionAccessRequest",
    },
    BulkSetSeatMovementRestrictionsWorkflow: {
      sample: {
        OrgSeatMovementRestrictions: [],
        Region: "",
      },
      sourceFile: "workflows/workflow-bulk-set-seat-movement-restrictions.go",
      sourceKind: "proto",
      sourceType: "support.BulkSetSeatMovementRestrictionsWorkflowRequest",
    },
    BulkSetZuoraMigrationStatus: {
      sample: {
        requests: [],
      },
      sourceFile: "workflows/workflow-bulk-set-zuora-migration-status.go",
      sourceKind: "proto",
      sourceType: "support.BulkSetZuoraMigrationStatusRequest",
    },
    BulkStartInspections: {
      sample: {
        MaxDaysInPast: 0,
        NumInspections: 0,
        OrgID: "",
        ParentWorkflowRunID: "",
        Region: "<support.Region>",
        SiteIDs: [],
        TemplateIds: [],
        UserIDs: [],
      },
      sourceFile: "workflows/workflow-bulk-start-inspections.go",
      sourceKind: "go-struct",
      sourceType: "BulkStartInspectionsRequest",
    },
    BulkSyncBillingAdminContacts: {
      sample: {
        batch_size: 0,
        billing_provider: "<subscriptionsv1.BillingProvider>",
        created_after: "<time.Time>",
        created_before: "<time.Time>",
        exclusion_list: [],
        number_of_batches: 0,
        page_token: "",
        region: "",
        run_syncs: false,
        subscription_type: "<adminsubscriptions.SubscriptionType>",
      },
      sourceFile: "workflows/workflow-bulk-sync-billing-admin-contacts.go",
      sourceKind: "proto",
      sourceType: "support.BulkSyncBillingAdminContactsRequest",
    },
    BulkSyncSubscriptionsWithZuora: {
      sample: {
        BatchSize: 0,
        BillingProvider: "<subscriptionsv1.BillingProvider>",
        EndYear: 0,
        ExclusionList: [],
        InclusionList: [],
        MaxParallelWindows: 0,
        NumberOfBatches: 0,
        Region: "",
        RunSyncs: false,
        StartYear: 0,
        SubscriptionType: "<adminsubscriptions.SubscriptionType>",
        WindowMonths: 0,
      },
      sourceFile: "workflows/workflow-bulk-sync-subscriptions-with-zuora.go",
      sourceKind: "go-struct",
      sourceType: "BulkSyncSubscriptionsWithZuoraRequest",
    },
    BulkSyncSubscriptionsWithZuoraWindow: {
      sample: {
        BatchSize: 0,
        BillingProvider: "<subscriptionsv1.BillingProvider>",
        CarriedBatchesProcessed: 0,
        CarriedFailedCount: 0,
        CarriedFailedItems: [],
        CarriedSkippedOrgCount: 0,
        CarriedTotalOrgsFound: 0,
        CreatedAfter: "<time.Time>",
        CreatedBefore: "<time.Time>",
        ExclusionList: [],
        InclusionList: [],
        NumberOfBatches: 0,
        PageToken: "",
        Region: "",
        RunSyncs: false,
        SubscriptionType: "<adminsubscriptions.SubscriptionType>",
      },
      sourceFile: "workflows/workflow-bulk-sync-subscriptions-with-zuora.go",
      sourceKind: "go-struct",
      sourceType: "BulkSyncSubscriptionsWithZuoraWindowRequest",
    },
    BulkTransferUsers: {
      sample: {
        n_batches: 0,
        sc_employee: {
          email: "",
          name: "",
        },
        users_to_transfer: [],
      },
      sourceFile: "workflows/workflow-transfer-multi-org-user.go",
      sourceKind: "proto",
      sourceType: "support.BulkTransferUsersRequest",
    },
    BulkTransferUsersSingleBatch: {
      sample: {
        n_batches: 0,
        sc_employee: {
          email: "",
          name: "",
        },
        users_to_transfer: [],
      },
      sourceFile: "workflows/workflow-transfer-multi-org-user.go",
      sourceKind: "proto",
      sourceType: "support.BulkTransferUsersRequest",
    },
    BulkUpdateContext: {
      sample: {
        Region: "<support.Region>",
        RequestReference: "",
        Requester: "",
        UserID: "",
      },
      sourceFile: "workflows/workflow-bulk-update-context.go",
      sourceKind: "go-struct",
      sourceType: "BulkUpdateContextRequest",
    },
    BulkUpdateContextProcessInspections: {
      sample: {
        InspectionResponse: "<client.GetInspectionResponse>",
        OrgID: "",
        Region: "<support.Region>",
        TemplateIDs: [],
        UserID: "",
      },
      sourceFile: "workflows/workflow-bulk-update-context.go",
      sourceKind: "go-struct",
      sourceType: "BulkUpdateContextProcessDocumentsRequest",
    },
    BulkUpdateContextProcessTemplates: {
      sample: {
        InspectionResponse: "<client.GetInspectionResponse>",
        OrgID: "",
        Region: "<support.Region>",
        TemplateIDs: [],
        UserID: "",
      },
      sourceFile: "workflows/workflow-bulk-update-context.go",
      sourceKind: "go-struct",
      sourceType: "BulkUpdateContextProcessDocumentsRequest",
    },
    BulkUpdateIdentity: {
      sample: {
        region: "",
        users_to_change: [],
      },
      sourceFile: "workflows/workflow-bulk-update-identity.go",
      sourceKind: "proto",
      sourceType: "support.BulkUpdateIdentityRequest",
    },
    BulkUpdateIdentitySingleBatch: {
      sample: {
        region: "",
        users_to_change: [],
      },
      sourceFile: "workflows/workflow-bulk-update-identity.go",
      sourceKind: "proto",
      sourceType: "support.BulkUpdateIdentityRequest",
    },
    BulkUpdateInspectionSite: {
      sample: {
        OrgID: "",
        Region: "<support.Region>",
        SiteMap: "<map>",
        UserIDs: [],
      },
      sourceFile: "workflows/workflow-bulk-update-inspection-site.go",
      sourceKind: "proto",
      sourceType: "support.BulkUpdateInspectionSiteRequest",
    },
    BulkUpdateInspectionTitle: {
      sample: {
        InspectionIDs: [],
        Region: "<support.Region>",
        RequestReference: "",
        Requester: "",
        TemplateIdentity: {
          OrgID: "",
          TemplateID: "",
        },
      },
      sourceFile: "workflows/workflow-bulk-update-inspection-title.go",
      sourceKind: "go-struct",
      sourceType: "BulkUpdateInspectionTitleRequest",
    },
    BulkUpdateInspectionTitleProcessInspections: {
      sample: {
        BackupID: "",
        BackupOperationName: "",
        InspectionIDs: [],
        Region: "<support.Region>",
      },
      sourceFile: "workflows/workflow-bulk-update-inspection-title.go",
      sourceKind: "go-struct",
      sourceType: "BulkUpdateInspectionTitleDocumentsRequest",
    },
    BulkUpdateIssueSite: {
      sample: {
        OrgID: "",
        Region: "<support.Region>",
        SiteMap: "<map>",
        UserID: "",
      },
      sourceFile: "workflows/workflow-bulk-update-issue-site.go",
      sourceKind: "proto",
      sourceType: "support.BulkUpdateIssueSiteRequest",
    },
    CleanupOrphanSoterUsersWorkflow: {
      sample: {
        region: "",
        run_delete: false,
        user_ids: [],
      },
      sourceFile: "workflows/workflow-cleanup-orphan-soter-users.go",
      sourceKind: "proto",
      sourceType: "support.CleanupOrphanSoterUsersRequest",
    },
    CloneDocument: {
      sample: {
        DocumentID: "",
        Domain: "<migration.OrgDataDomainType>",
        Env: "",
        IDMap: "<map>",
        SourceOrgID: "",
        SourceRegion: "",
        TargetRegion: "",
      },
      sourceFile: "internal/migration/workflows/clone_document.go",
      sourceKind: "go-struct",
      sourceType: "CloneDocumentRequest",
    },
    CloneOrganisation: {
      sample: {
        CloneName: "",
        Env: "",
        OrgID: "",
        Requester: "",
        SourceRegion: "",
        TargetRegion: "",
        UserID: "",
      },
      sourceFile: "workflows/workflow-org-clone.go",
      sourceKind: "go-struct",
      sourceType: "CloneOrganisationRequest",
    },
    CloneSubscriptions: {
      sample: {
        CopyRequest: "<OrgDataCopyRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_copy.go",
      sourceKind: "go-struct",
      sourceType: "CopyWorkflowRequest",
    },
    ContentLibraryAlgoliaReindex: {
      sample: {
        batch_size: 0,
        sync_time: "",
      },
      sourceFile: "workflows/workflow-content-library-algolia-reindex.go",
      sourceKind: "proto",
      sourceType: "support.ContentLibraryAlgoliaReindexRequest",
    },
    ContentLibraryBulkTestOrgDeletion: {
      sample: {
        DeletionEnabled: false,
        Env: "",
        Region: "",
      },
      sourceFile:
        "workflows/workflow-content-library-bulk-test-org-deletion.go",
      sourceKind: "proto",
      sourceType: "support.ContentLibraryBulkOrgDeletionRequest",
    },
    ContentLibraryBulkUpdateProductImages: {
      sample: {
        products: [],
      },
      sourceFile:
        "workflows/workflow-content-library-bulk-update-product-images.go",
      sourceKind: "proto",
      sourceType: "support.ContentLibraryBulkUpdateProductImagesRequest",
    },
    ContentLibraryPublisherReindex: {
      sample: {
        batch_size: 0,
      },
      sourceFile: "workflows/workflow-content-library-publisher-reindex.go",
      sourceKind: "proto",
      sourceType: "support.ContentLibraryPublisherReindexRequest",
    },
    ContentLibraryThumbnailBackfill: {
      sample: {},
      sourceFile: "workflows/workflow-content-library-thumbnail-backfill.go",
      sourceKind: "proto",
      sourceType: "support.ContentLibraryThumbnailBackfillRequest",
    },
    CopyAnalyticsActions: {
      sample: {
        CopyRequest: "<OrgDataCopyRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_copy.go",
      sourceKind: "go-struct",
      sourceType: "CopyWorkflowRequest",
    },
    CopyAnalyticsInspections: {
      sample: {
        CopyRequest: "<OrgDataCopyRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_copy.go",
      sourceKind: "go-struct",
      sourceType: "CopyWorkflowRequest",
    },
    CopyAnalyticsIssues: {
      sample: {
        CopyRequest: "<OrgDataCopyRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_copy.go",
      sourceKind: "go-struct",
      sourceType: "CopyWorkflowRequest",
    },
    CopyAnalyticsSites: {
      sample: {
        CopyRequest: "<OrgDataCopyRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_copy.go",
      sourceKind: "go-struct",
      sourceType: "CopyWorkflowRequest",
    },
    CopyDomainDataWorkflow: {
      sample: {
        CopyRequest: "<OrgDataCopyRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_copy.go",
      sourceKind: "go-struct",
      sourceType: "CopyWorkflowRequest",
    },
    CopyOrganisation: {
      sample: {
        CopyRequest: "<OrgDataCopyRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_copy.go",
      sourceKind: "go-struct",
      sourceType: "CopyWorkflowRequest",
    },
    CopyPermissionSets: {
      sample: {
        CopyRequest: "<OrgDataCopyRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_copy.go",
      sourceKind: "go-struct",
      sourceType: "CopyWorkflowRequest",
    },
    CopyPrivileges: {
      sample: {
        CopyRequest: "<OrgDataCopyRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_copy.go",
      sourceKind: "go-struct",
      sourceType: "CopyWorkflowRequest",
    },
    CopyTraining: {
      sample: {
        CopyRequest: "<OrgDataCopyRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_copy.go",
      sourceKind: "go-struct",
      sourceType: "CopyWorkflowRequest",
    },
    CreateOrg: {
      sample: {
        CouponCode: "",
        EmailAddress: "",
        EmailConfiguration: {
          Domain: "",
          LocalAddress: "",
          SubAddress: "",
        },
        ExcludeFromAnalytics: false,
        FirstName: "",
        LastName: "",
        OrgName: "",
        Password: "",
        Region: "",
        SaveAccountInfo: false,
        SubscriptionState: "",
        SubscriptionTerm: "",
        TestAccount: false,
      },
      sourceFile: "workflows/workflow-bulk-create-org.go",
      sourceKind: "go-struct",
      sourceType: "CreateOrgRequest",
    },
    DeactivateOrgSubscriptionState: {
      sample: {
        OrgID: "",
        SourceRegion: "",
      },
      sourceFile: "workflows/workflow-org-deactivate-subscription.go",
      sourceKind: "proto",
      sourceType: "support.DeactivateOrgSubscriptionStateRequest",
    },
    DeleteAnalyticsActions: {
      sample: {
        DeletionRequest: "<OrgDataDeletionRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_delete.go",
      sourceKind: "go-struct",
      sourceType: "DeletionWorkflowRequest",
    },
    DeleteAnalyticsInspections: {
      sample: {
        DeletionRequest: "<OrgDataDeletionRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_delete.go",
      sourceKind: "go-struct",
      sourceType: "DeletionWorkflowRequest",
    },
    DeleteAnalyticsIssues: {
      sample: {
        DeletionRequest: "<OrgDataDeletionRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_delete.go",
      sourceKind: "go-struct",
      sourceType: "DeletionWorkflowRequest",
    },
    DeleteAnalyticsSites: {
      sample: {
        DeletionRequest: "<OrgDataDeletionRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_delete.go",
      sourceKind: "go-struct",
      sourceType: "DeletionWorkflowRequest",
    },
    DeleteDataGovernanceQueues: {
      sample: {
        OlderThanDays: 0,
      },
      sourceFile: "workflows/workflow-org-deletion.go",
      sourceKind: "go-struct",
      sourceType: "DeleteDataGovernanceQueuesRequest",
    },
    DeleteDomainDataWorkflow: {
      sample: {
        DeletionRequest: "<OrgDataDeletionRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_delete.go",
      sourceKind: "go-struct",
      sourceType: "DeletionWorkflowRequest",
    },
    DeleteOrganisationDocument: {
      sample: {
        DeletionRequest: "<OrgDataDeletionRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_delete.go",
      sourceKind: "go-struct",
      sourceType: "DeletionWorkflowRequest",
    },
    DeleteUserForIM1989: {
      sample: {
        Region: "",
        UserID: "",
      },
      sourceFile: "workflows/workflow-bulk-delete-users.go",
      sourceKind: "proto",
      sourceType: "support.DeleteUserForIM1989Request",
    },
    DowngradeOrgDeactivateUsers: {
      sample: {
        NumberOfBatches: 0,
        OrgID: "",
        Region: "",
        UserIDs: [],
      },
      sourceFile: "workflows/workflow-downgrade-org-deactivate-users.go",
      sourceKind: "go-struct",
      sourceType: "DowngradeOrgDeactivateUsersRequest",
    },
    GenerateDomainDataDelta: {
      sample: {
        Domain: "<migration.OrgDataDomainType>",
        MapRequest: "<OrgDataMapRequest>",
      },
      sourceFile: "internal/migration/workflows/org_data_map.go",
      sourceKind: "go-struct",
      sourceType: "GenerateDomainDataDeltaRequest (= MappingWorkflowRequest)",
    },
    MapAnalyticsActions: {
      sample: {
        Domain: "<migration.OrgDataDomainType>",
        MapRequest: "<OrgDataMapRequest>",
      },
      sourceFile: "internal/migration/workflows/org_data_map.go",
      sourceKind: "go-struct",
      sourceType: "MappingWorkflowRequest",
    },
    MapAnalyticsInspections: {
      sample: {
        Domain: "<migration.OrgDataDomainType>",
        MapRequest: "<OrgDataMapRequest>",
      },
      sourceFile: "internal/migration/workflows/org_data_map.go",
      sourceKind: "go-struct",
      sourceType: "MappingWorkflowRequest",
    },
    MapAnalyticsIssues: {
      sample: {
        Domain: "<migration.OrgDataDomainType>",
        MapRequest: "<OrgDataMapRequest>",
      },
      sourceFile: "internal/migration/workflows/org_data_map.go",
      sourceKind: "go-struct",
      sourceType: "MappingWorkflowRequest",
    },
    MapAnalyticsSites: {
      sample: {
        Domain: "<migration.OrgDataDomainType>",
        MapRequest: "<OrgDataMapRequest>",
      },
      sourceFile: "internal/migration/workflows/org_data_map.go",
      sourceKind: "go-struct",
      sourceType: "MappingWorkflowRequest",
    },
    MapDomainDataWorkflow: {
      sample: {
        Domain: "<migration.OrgDataDomainType>",
        MapRequest: "<OrgDataMapRequest>",
      },
      sourceFile: "internal/migration/workflows/org_data_map.go",
      sourceKind: "go-struct",
      sourceType: "MappingWorkflowRequest",
    },
    MapPrivileges: {
      sample: {
        Domain: "<migration.OrgDataDomainType>",
        MapRequest: "<OrgDataMapRequest>",
      },
      sourceFile: "internal/migration/workflows/org_data_map.go",
      sourceKind: "go-struct",
      sourceType: "MappingWorkflowRequest",
    },
    MigrateInspectionQuota: {
      sample: {
        batch_size: 0,
        environment: "",
        org_ids: [],
        region: "",
        s3_file_name: "",
      },
      sourceFile: "workflows/workflow-migrate-inspection-quota.go",
      sourceKind: "proto",
      sourceType: "support.MigrateInspectionQuotaRequest",
    },
    MigrateToZuoraSingleOrg: {
      sample: {
        BatchName: "",
        OrganisationID: "",
        Region: "",
        ShouldPersist: false,
      },
      sourceFile: "workflows/workflow-migrate-to-zuora-single.go",
      sourceKind: "go-struct",
      sourceType: "MigrateToZuoraSingleOrgRequest",
    },
    OrgDataClone: {
      sample: {
        DataCopyID: "",
        Env: "",
        IsDelta: false,
        MappingResults: "<map>",
        OrgID: "",
        Purpose: "",
        SourceRegion: "",
        TargetRegion: "",
        UserID: "",
      },
      sourceFile: "internal/migration/types.go",
      sourceKind: "go-struct",
      sourceType: "OrgDataCloneRequest (= CopyRequest)",
    },
    OrgDataCopy: {
      sample: {
        DataCopyID: "",
        Env: "",
        IsDelta: false,
        MappingResults: "<map>",
        OrgID: "",
        Purpose: "",
        SourceRegion: "",
        TargetRegion: "",
        UserID: "",
      },
      sourceFile: "internal/migration/types.go",
      sourceKind: "go-struct",
      sourceType: "OrgDataCopyRequest (= CopyRequest)",
    },
    OrgDataDeletion: {
      sample: {
        DataDeletionID: "",
        Env: "",
        MappingResults: "<map>",
        OrgID: "",
        Purpose: "",
        SourceRegion: "",
        TargetRegion: "",
      },
      sourceFile: "internal/migration/types.go",
      sourceKind: "go-struct",
      sourceType: "OrgDataDeletionRequest (= DeletionRequest)",
    },
    OrgDataMap: {
      sample: {
        DataMapID: "",
        Env: "",
        OrgID: "",
        PrevMapID: "",
        Purpose: "",
        SourceRegion: "",
      },
      sourceFile: "internal/migration/types.go",
      sourceKind: "go-struct",
      sourceType: "OrgDataMapRequest (= MapRequest)",
    },
    OrgDeletion: {
      sample: {
        Env: "",
        OrgID: "",
        PrevMapID: "",
        Purpose: "",
        Region: "",
        RequestReference: "",
        Requester: "",
      },
      sourceFile: "workflows/workflow-org-deletion.go",
      sourceKind: "go-struct",
      sourceType: "OrgDeletionRequest",
    },
    OrgMapOnly: {
      sample: {
        TargetRegion: "",
      },
      sourceFile: "workflows/activities-org-deletion.go",
      sourceKind: "go-struct",
      sourceType: "OrgMapOnlyRequest (= MapOrganisationRequest)",
    },
    OrgMigration: {
      sample: {
        Env: "",
        OrgID: "",
        PrevMappingID: "",
        RequestReference: "",
        Requester: "",
        SourceRegion: "",
        TargetRegion: "",
        UnblockUpToCleanUp: false,
        UserID: "",
      },
      sourceFile: "workflows/workflow-org-migration.go",
      sourceKind: "go-struct",
      sourceType: "OrgMigrationRequest",
    },
    ProcessUpdateInspectionSiteByUserID: {
      sample: {
        Region: "<support.Region>",
        SiteMap: "<map>",
        User: {
          Email: "",
          ID: "",
          OrgID: "",
        },
        ValidSiteMap: "<map>",
      },
      sourceFile: "workflows/workflow-bulk-update-inspection-site.go",
      sourceKind: "proto",
      sourceType: "support.ProcessUpdateInspectionSiteByUserIDRequest",
    },
    ProvisionAiCreditsWorkflow: {
      sample: {
        MaxActiveUsers: 0,
        OrgS12ID: "",
        Region: "",
      },
      sourceFile: "workflows/workflow-provision-ai-credits.go",
      sourceKind: "go-struct",
      sourceType: "ProvisionAiCreditsRequest",
    },
    ProvisionSCCareOrg: {
      sample: {
        BrokerEmail: "",
        Country: "",
        CouponCode: "",
        Currency: "",
        EmailAddress: "",
        ExcludeFromAnalytics: false,
        FirstName: "",
        LastName: "",
        OrgName: "",
        Password: "",
        PolicyNumber: "",
        PostalCode: "",
        Region: "",
        ShouldSendSCCareWelcomeEmail: false,
        State: "",
        SubscriptionTerm: "",
        TemplateIds: [],
        TestAccount: false,
      },
      sourceFile: "workflows/workflow-provision-sc-care-org.go",
      sourceKind: "go-struct",
      sourceType: "ProvisionSCCareOrgRequest",
    },
    ProvisionSupportUser: {
      sample: {
        email: "",
        first_name: "",
        last_name: "",
        reason: "",
        region: "",
        replacement_user_id: "",
        target_org_id: "",
      },
      sourceFile: "workflows/workflow-provision-support-user.go",
      sourceKind: "proto",
      sourceType: "support.ProvisionSupportUserRequest",
    },
    PurgeDataGovernanceQueue: {
      sample: {
        QueueName: "",
      },
      sourceFile: "workflows/workflow-org-migration.go",
      sourceKind: "proto",
      sourceType: "support.PurgeDataGovernanceQueueRequest",
    },
    ReactivateOrg: {
      sample: {
        Env: "",
        OrgID: "",
        SourceRegion: "",
        TargetRegion: "",
      },
      sourceFile: "workflows/workflow-org-reactivate.go",
      sourceKind: "go-struct",
      sourceType: "ReactivateOrgRequest",
    },
    ReactivateOrgSubscriptionState: {
      sample: {
        Env: "",
        OrgID: "",
        SourceRegion: "",
        TargetRegion: "",
      },
      sourceFile: "workflows/workflow-org-reactivate.go",
      sourceKind: "proto",
      sourceType: "support.ReactivateOrgSubscriptionStateRequest",
    },
    RemediateTemplatesAccess: {
      sample: {
        LeaveViewPermission: false,
        LiveRun: false,
        Region: "",
        TemplateIDs: [],
      },
      sourceFile: "workflows/workflow-reset-org-template-access.go",
      sourceKind: "proto",
      sourceType: "support.RemediateTemplatesAccessRequest",
    },
    ResetOrgTemplatesAccess: {
      sample: {
        // NOTE: this proto field is genuinely declared camelCase (non-standard) —
        // do not snake_case it like the other fields below.
        backupFolderName: "",
        org_id: "",
        region: "",
      },
      sourceFile: "workflows/workflow-reset-org-template-access.go",
      sourceKind: "proto",
      sourceType: "support.ResetOrgTemplatesAccessRequest",
    },
    ResetTemplatesAccess: {
      sample: {
        BackupFolderName: "",
        HasFullTemplateAccess: false,
        Region: "",
        TemplateIDs: [],
      },
      sourceFile: "workflows/workflow-reset-org-template-access.go",
      sourceKind: "go-struct",
      sourceType: "ResetTemplatesAccessRequest",
    },
    SSOKeycloakEnvCreation: {
      sample: {
        Env: {
          Auth0CallbackURL: "",
          Auth0Domain: "",
          Namespace: "",
        },
        KeyCloak: {
          Realm: "",
          RealmID: "",
          SAMLClientID: "",
          SAMLClientName: "",
          SSO: {
            AssertionConsumerURL: "",
            ClientID: "",
            SignInURL: "",
            SigningCert: "",
          },
        },
        Org: {
          Auth0ConnectionName: "",
          ID: "",
          Name: "",
          S12ID: "",
        },
        Owner: {
          Email: "",
          FirstName: "",
          LastName: "",
          Password: "",
          S12ID: "",
        },
      },
      sourceFile: "workflows/workflow-sso-keycloak-env-creation.go",
      sourceKind: "proto",
      sourceType: "support.SSOKeycloakEnvCreationRequest",
    },
    SoterCopyWorkflow: {
      sample: {
        CopyRequest: "<OrgDataCopyRequest>",
        Domain: "<migration.OrgDataDomainType>",
      },
      sourceFile: "internal/migration/workflows/org_data_copy.go",
      sourceKind: "go-struct",
      sourceType: "CopyWorkflowRequest",
    },
    SoterMappingWorkflow: {
      sample: {
        Domain: "<migration.OrgDataDomainType>",
        MapRequest: "<OrgDataMapRequest>",
      },
      sourceFile: "internal/migration/workflows/org_data_map.go",
      sourceKind: "go-struct",
      sourceType: "MappingWorkflowRequest",
    },
    SyncOrgBillingAdminContactsWorkflow: {
      sample: {
        OrgS12ID: "",
        Region: "",
      },
      sourceFile: "workflows/workflow-sync-org-billing-admin-contacts.go",
      sourceKind: "go-struct",
      sourceType: "SyncOrgBillingAdminContactsRequest",
    },
    SyncProductCatalogToAPISubscriptions: {
      sample: {
        batch_size: 0,
        effective_date: "<time.Time>",
        filters: {
          BillingPeriods: [],
          ComponentTypes: [],
          Currencies: [],
          EffectiveDate: "<time.Time>",
          IncludeInactive: false,
          IncludeLegacy: false,
          Statuses: [],
          Tiers: [],
        },
        product_fetch_concurrency: 0,
        regions: [],
        upsert_concurrency: 0,
      },
      sourceFile: "workflows/workflow-sync-product-catalog.go",
      sourceKind: "proto",
      sourceType: "support.SyncProductCatalogRequest",
    },
    TransferUser: {
      sample: {
        region: "<Region>",
        source_org_id: "",
        source_user_id: "",
        target_org_id: "",
        target_seat_type: "<s12.common.SubscriptionSeatTypes>",
        target_user_id: "",
      },
      sourceFile: "workflows/workflow-transfer-multi-org-user.go",
      sourceKind: "proto",
      sourceType: "support.TransferUserRequest",
    },
    TransferUserForIM1989: {
      sample: {
        region: "<Region>",
        source_org_id: "",
        source_user_id: "",
        target_org_id: "",
        target_seat_type: "<s12.common.SubscriptionSeatTypes>",
        target_user_id: "",
      },
      sourceFile: "workflows/workflow-transfer-multi-org-user.go",
      sourceKind: "proto",
      sourceType: "support.TransferUserRequest",
    },
  };
